// src/context/AuthContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { axiosInstance, attachAxiosInterceptors } from "../axiosInstance";
import { UIContext } from "./UIContext";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);

  // We'll use openAuthDrawer to open the AuthDrawer on session expiry
  const { openAuthDrawer } = useContext(UIContext);

  // 1. On mount, read tokens from localStorage
  useEffect(() => {
    const storedAccess = localStorage.getItem("accessToken");
    const storedRefresh = localStorage.getItem("refreshToken");
    if (storedAccess && storedRefresh) {
      setAccessToken(storedAccess);
      setRefreshToken(storedRefresh);
      // decode user
      try {
        const decoded = JSON.parse(atob(storedAccess.split(".")[1]));
        setUser({ id: decoded.userId, role: decoded.role });
      } catch (err) {
        console.error("Error decoding stored token:", err);
      }
    }
  }, []);

  // Debug effect
  useEffect(() => {
    console.log("AuthContext: Access token changed:", accessToken);
  }, [accessToken]);

  // 2. useCallback for stable references
  const logout = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axiosInstance.post("/api/auth/login", {
        email,
        password,
      });
      const newAccessToken = response.data.accessToken;
      const newRefreshToken = response.data.refreshToken;

      setAccessToken(newAccessToken);
      setRefreshToken(newRefreshToken);
      localStorage.setItem("accessToken", newAccessToken);
      localStorage.setItem("refreshToken", newRefreshToken);

      // decode
      const decoded = JSON.parse(atob(newAccessToken.split(".")[1]));
      setUser({ id: decoded.userId, role: decoded.role });

      console.log("Login success:", decoded.userId);
    } catch (error) {
      console.error("AuthContext.login: Login failed:", error);
      throw error;
    }
  }, []);

  const signup = useCallback(
    async (firstName, lastName, username, email, password) => {
      try {
        await axiosInstance.post("/api/auth/register", {
          firstName,
          lastName,
          username,
          email,
          password,
        });
      } catch (error) {
        console.error("Signup failed:", error);
        throw error;
      }
    },
    []
  );

  const refreshAuthToken = useCallback(async () => {
    try {
      const storedRefresh =
        localStorage.getItem("refreshToken") || refreshToken;
      if (!storedRefresh) throw new Error("No refresh token available");

      const response = await axiosInstance.post("/api/auth/refresh", {
        refreshToken: storedRefresh,
      });
      setAccessToken(response.data.accessToken);
      localStorage.setItem("accessToken", response.data.accessToken);

      return response.data.accessToken;
    } catch (error) {
      logout();
      throw error;
    }
  }, [refreshToken, logout]);

  const onRequestPasswordReset = useCallback(async (email) => {
    try {
      if (!email) {
        throw new Error("No email provided for password reset.");
      }
      // Call the back-end route
      const response = await axiosInstance.post(
        "/api/auth/request-password-reset",
        {
          email,
        }
      );
      // If server responds with 200, return response message
      return response.data.message;
    } catch (error) {
      console.error("AuthContext: requestPasswordReset failed:", error);
      throw error; // Let caller show an error
    }
  }, []);

  // in AuthContext.js
  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      // e.g. { firstName, lastName, gender, birthday: { month, day } }
      if (!accessToken) {
        console.warn("Cannot change password: user not logged in");
        return;
      }
      try {
        // const response = await axiosInstance.post("/api/auth/login", { email, password });
        const response = await axiosInstance.post(
          "/api/auth/change-password",
          { currentPassword, newPassword },
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        return response.data.message;
      } catch (error) {
        console.error("AuthContext: Failed to change password", error);
      }
    },
    [accessToken]
  );

  const deleteAccount = useCallback(async () => {
    if (!accessToken) {
      throw new Error("No user is logged in.");
    }
    try {
      await axiosInstance.delete("/api/auth/account", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // On success, log out
      logout();
    } catch (err) {
      console.error("deleteAccount error:", err);
      throw err;
    }
  }, [accessToken, logout]);

  // We'll memoize a function for the interceptor to call to open the drawer
  const handleOpenDrawer = useCallback(
    (msg) => {
      openAuthDrawer(msg);
    },
    [openAuthDrawer]
  );

  // 3. Attach interceptors
  useEffect(() => {
    attachAxiosInterceptors(refreshAuthToken, logout, handleOpenDrawer);
  }, [refreshAuthToken, logout, handleOpenDrawer]);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        refreshToken,
        refreshAuthToken,
        login,
        signup,
        logout,
        onRequestPasswordReset,
        changePassword,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
